import { normalizeToV3 } from './chordModelMigration.js';
import { formatNote } from './noteFormatting.js';
import { equalNotes } from './noteComparison.js';
export const getQualitySuffix = (qualityValue, hasExtension = false, extension) => {
    if (qualityValue === null)
        return "";
    switch (qualityValue) {
        case "": // Represents an implied major quality (e.g., from a parsed "C7") or no quality for simple fundamental.
        case "major":
            // For major 6th chords, don't add "maj" - just return empty string
            if (extension === "6")
                return "";
            return hasExtension ? "maj" : ""; // Use maj for Major with extension (Cmaj7), "" for C (major triad)
        case "minor":
            return hasExtension ? "m" : "m"; // Use m for Minor with extension (Cm7), and m for Cm (minor triad)
        case "minorMajor":
            return "mM"; // For CmM7, CmM9 etc. The extension is handled by buildChordString
        case "diminished":
            return "dim"; // Always use "dim" for consistency (Cdim, Cdim7, Cdim9)
        case "augmented":
            return "aug"; // Always use "aug" for consistency (Caug, Caug7, Caug9)
        case "sus4":
            return "sus4";
        case "sus2":
            return "sus2";
        case "power":
            return "5";
        default:
            return "";
    }
};
/**
 * Build a chord string from a chord model.
 *
 * Accepts V1, V2, and V3 chord models.
 * Internally converts to V3 for processing.
 *
 * @param chord - Chord model in any format
 * @returns Chord string (e.g., "Cmaj7", "F♯m/C♯")
 */
export const buildChordString = (chord) => {
    // Normalize to V3 for internal processing
    const v3 = normalizeToV3(chord);
    // Handle null root
    if (!v3.root) {
        return "- - -";
    }
    // Format root note
    const rootStr = formatNote(v3.root);
    let chordStr = rootStr;
    const isSusQuality = v3.quality === "sus2" || v3.quality === "sus4";
    if (v3.isDominant) {
        let effectiveExtension = v3.extension;
        if (effectiveExtension === null) {
            effectiveExtension = "7";
        }
        if (effectiveExtension) {
            chordStr += effectiveExtension;
        }
        if (isSusQuality) {
            chordStr += v3.quality;
        }
    }
    else {
        let actualExtension = v3.extension;
        if (isSusQuality) {
            actualExtension = null;
        }
        const hasActualExtension = actualExtension !== null;
        if (v3.quality === "diminished" && actualExtension === "7") {
            chordStr += "dim" + actualExtension; // Directly form Cdim7
        }
        else {
            chordStr += getQualitySuffix(v3.quality, hasActualExtension, actualExtension);
            if (hasActualExtension) {
                chordStr += actualExtension;
            }
        }
    }
    const adds = v3.adds ?? [];
    if (adds.length > 0) {
        const extractNumber = (addStr) => {
            const match = addStr.match(/\d+/);
            return match ? parseInt(match[0], 10) : Infinity;
        };
        const sortedAdds = [...adds].sort((a, b) => extractNumber(a) - extractNumber(b));
        chordStr += sortedAdds.join('');
    }
    const alterations = v3.alterations ?? [];
    if (alterations.length > 0) {
        if (alterations.length >= 4) {
            chordStr += "alt";
        }
        else {
            const parseAlteration = (altStr) => {
                const numMatch = altStr.match(/\d+/);
                const num = numMatch ? parseInt(numMatch[0], 10) : Infinity;
                if (altStr.startsWith('b') || altStr.startsWith('♭'))
                    return { num, type: 'b' };
                if (altStr.startsWith('#') || altStr.startsWith('♯'))
                    return { num, type: '#' };
                return { num, type: 'n' };
            };
            const sortedAlterations = [...alterations].sort((a, b) => {
                const parsedA = parseAlteration(a);
                const parsedB = parseAlteration(b);
                if (parsedA.num !== parsedB.num) {
                    return parsedA.num - parsedB.num;
                }
                if (parsedA.type === 'b' && parsedB.type === '#')
                    return -1;
                if (parsedA.type === '#' && parsedB.type === 'b')
                    return 1;
                return 0;
            });
            // Alterations directly after a bare root are ambiguous ("C♭5" reads as
            // a C♭ power chord, "F♯9" as F♯ dominant 9), so parenthesize them
            // (KNOWN_BUGS.md Bug 1).
            const alterationsStr = sortedAlterations.join('');
            chordStr += chordStr === rootStr ? `(${alterationsStr})` : alterationsStr;
        }
    }
    // Add bass note (slash chord)
    if (v3.bass && !equalNotes(v3.bass, v3.root)) {
        chordStr += `/${formatNote(v3.bass)}`;
    }
    return chordStr;
};
